/**
 * Client for workers/cron's SMTP settings + schedule endpoints.
 *
 * Server lives at config.cronWorkerBase. JWT-auth on every call via the
 * Bearer header from the session.
 */

import type {
  SMTPSettingsInput,
  SMTPSettingsPublic,
} from "@agencylens/shared";
import { config } from "./config.js";

export interface SmtpGetResponse {
  smtp: SMTPSettingsPublic | null;
}

export interface SmtpPutResponse {
  smtp: SMTPSettingsPublic;
}

export interface SmtpTestResponse {
  ok: boolean;
  error?: string;
  smtp: SMTPSettingsPublic;
}

async function request<T>(
  path: string,
  jwt: string,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(`${config.cronWorkerBase}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${jwt}`,
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  const text = await res.text();
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error(`Cron worker non-JSON ${res.status}: ${text.slice(0, 200)}`);
  }
  if (!res.ok) {
    const err = data as { error?: string; hint?: string };
    const e = new Error(err.error ?? `HTTP ${res.status}`);
    Object.assign(e, err);
    (e as Error & { status?: number }).status = res.status;
    throw e;
  }
  return data as T;
}

export async function getSmtpSettings(jwt: string): Promise<SmtpGetResponse> {
  return request<SmtpGetResponse>("/smtp-settings", jwt, { method: "GET" });
}

export async function putSmtpSettings(
  jwt: string,
  input: SMTPSettingsInput,
): Promise<SmtpPutResponse> {
  return request<SmtpPutResponse>("/smtp-settings", jwt, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function deleteSmtpSettings(jwt: string): Promise<void> {
  await request<{ ok: true }>("/smtp-settings", jwt, { method: "DELETE" });
}

export async function testSmtpConnection(
  jwt: string,
): Promise<SmtpTestResponse> {
  return request<SmtpTestResponse>("/smtp-settings/test", jwt, {
    method: "POST",
  });
}
